import React, { useEffect, useRef, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import TargetTitle from '../../activation-code/components/TargetTitle';
import { getStatusDisplay } from '../../activation-code/utils/activationCodeUtils';
import { useByUser, useSummaryOptions } from '../hooks/useActivationReport';
import { useReportList } from '../hooks/useReportList';
import { activationReportService } from '../services/activationReportService';
import { ReportFilters, UserRow } from '../types';
import { exportFileName, exportFilters, userExportRows, userWorkbookFromRows } from '../utils/exportReport';
import { DELETED_USER, formatDateTime } from '../utils/format';
import ExportButton from './ExportButton';
import Pagination from './Pagination';
import ReportState from './ReportState';

interface ByUserTabProps {
  filters: ReportFilters;
  onChange: (patch: Partial<ReportFilters>) => void;
}

const SEARCH_DELAY_MS = 400;

const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase';
const joinNames = (list: { name: string }[]) => (list.length ? list.map((x) => x.name).join(', ') : '—');
const userName = (row: UserRow) => row.user.name || DELETED_USER;

const ByUserTab: React.FC<ByUserTabProps> = ({ filters, onChange }) => {
  const setPage = (p: number) => onChange({ page: p });
  const { data, page, perPage, stateProps } = useReportList(useByUser(filters), filters, setPage);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const summaryOptions = useSummaryOptions(filters.school_id);

  // So sánh theo bản đã cắt: q có khoảng trắng hai đầu từ URL không được coi là "khác" khi mount
  const filterQ = (filters.q ?? '').trim();
  const [value, setValue] = useState(filters.q ?? '');
  const debounced = useDebounce(value, SEARCH_DELAY_MS);

  // Đọc q hiện tại qua ref để effect gửi không chạy lại (và không bắn lại q cũ) khi q đổi từ ngoài
  const filterQRef = useRef(filterQ);
  filterQRef.current = filterQ;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // "Xoá lọc" xoá q từ ngoài: ô tìm kiếm phải trống theo.
  // Giữ nguyên chữ đang gõ nếu chỉ khác khoảng trắng, kẻo nuốt dấu cách cuối khi người dùng đang gõ tiếp.
  useEffect(() => {
    setValue((v) => (v.trim() === filterQ ? v : filterQ));
  }, [filterQ]);

  // Chỉ gửi khi đã "lắng" (debounced bắt kịp value) và bản đã cắt khác q đang áp dụng, nên mount không gọi onChange.
  // Chỉ gõ dấu cách nghĩa là bỏ tìm kiếm.
  useEffect(() => {
    const q = debounced.trim();
    if (debounced === value && q !== filterQRef.current) {
      onChangeRef.current({ q: q || undefined });
    }
  }, [debounced, value]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <input
          type="search"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Tìm theo tên, SĐT, email"
          aria-label="Tìm theo tên, SĐT, email"
          className="w-full max-w-sm px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <ExportButton
          onExport={async () => {
            // Tab này bung mỗi người thành nhiều dòng nên không dùng exportList (hàm đó giả định
            // 1 bản ghi = 1 dòng). Tự gọi API để biết cả cờ cắt của API lẫn của bản thân khi bung dòng.
            const res = await activationReportService.byUser(exportFilters(filters), { export: true });
            const { data, truncated } = userExportRows(res.items);
            return {
              workbook: userWorkbookFromRows(data, filters, summaryOptions),
              truncated: res.truncated || truncated,
            };
          }}
          fileName={exportFileName('theo-nguoi-dung', filters)}
        />
      </div>

      <ReportState {...stateProps}>
        {data && (
          <div>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-10 px-4 py-3" aria-label="Mở rộng" />
                      <th className={TH}>Họ tên</th>
                      <th className={TH}>Tên đăng nhập</th>
                      <th className={TH}>SĐT</th>
                      <th className={TH}>Email</th>
                      <th className={TH}>Trường</th>
                      <th className={TH}>Lớp</th>
                      <th className={`${TH} text-right`}>Lượt kích hoạt</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.items.map((row) => {
                      const id = row.user.id;
                      const isOpen = expanded.has(id);
                      const name = userName(row);
                      return (
                        <React.Fragment key={id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              <button
                                type="button"
                                onClick={() => toggle(id)}
                                aria-expanded={isOpen}
                                aria-label={`${isOpen ? 'Ẩn' : 'Xem'} mã của ${name}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                {isOpen ? '▾' : '▸'}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{name}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{row.user.user_name || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{row.user.phone || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{row.user.email || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{joinNames(row.schools)}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{joinNames(row.classes)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {row.activations.toLocaleString('vi-VN')}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-gray-50">
                              <td />
                              <td colSpan={7} className="px-4 py-3">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead>
                                    <tr>
                                      <th className={TH}>Mã</th>
                                      <th className={TH}>Sách/Bộ sách</th>
                                      <th className={TH}>Ngày dùng</th>
                                      <th className={TH}>Trạng thái</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {row.codes.map((c) => {
                                      const status = getStatusDisplay(c.status);
                                      return (
                                        <tr key={c.code}>
                                          <td className="px-4 py-2 text-sm font-mono text-gray-900">{c.code}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">
                                            <TargetTitle item={c} />
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                                            {formatDateTime(c.used_at)}
                                          </td>
                                          <td className="px-4 py-2 text-sm">
                                            <span
                                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.badgeClass}`}
                                            >
                                              {status.label}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination page={page} perPage={perPage} total={data.total} onPageChange={setPage} />
          </div>
        )}
      </ReportState>
    </div>
  );
};

export default ByUserTab;
